import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Trash2, Check, Pen, Eraser, X } from "lucide-react";
import { toast } from "sonner";
import { uploadImage, fileUrl } from "@/lib/api";

/**
 * Finger / stylus drawing pad. On save, flattens the canvas to a PNG and
 * uploads it via `/api/upload`, then calls `onSave(path)` with the stored
 * object-storage path so the parent can attach it to a new note's `images`.
 *
 * The canvas is sized via a ResizeObserver (not a one-off effect) so late
 * dialog mounts and viewport changes still result in a correctly-sized
 * drawing surface. Keeping a backing-store that matches `devicePixelRatio`
 * is critical — otherwise strokes look blurry on retina displays.
 */
export default function DrawingPad({ open, onOpenChange, onSave }) {
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const [tool, setTool] = useState("pen"); // pen | eraser
  const [color, setColor] = useState("#1C1917");
  const [size, setSize] = useState(3);
  const [uploading, setUploading] = useState(false);
  const [ready, setReady] = useState(false);
  const drawing = useRef(false);
  const lastPt = useRef(null);

  // Re-fit the canvas to its container. Preserves the current drawing by
  // copying the old backing-store onto the new one before swapping.
  const fit = useCallback(() => {
    const c = canvasRef.current;
    const wrap = wrapperRef.current;
    if (!c || !wrap) return;
    const rect = wrap.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) return;
    const dpr = window.devicePixelRatio || 1;
    const prev = document.createElement("canvas");
    prev.width = c.width || 0; prev.height = c.height || 0;
    if (c.width && c.height) prev.getContext("2d").drawImage(c, 0, 0);
    c.width = Math.floor(rect.width * dpr);
    c.height = Math.floor(rect.height * dpr);
    c.style.width = rect.width + "px";
    c.style.height = rect.height + "px";
    const ctx = c.getContext("2d");
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, c.width, c.height);
    if (prev.width && prev.height) ctx.drawImage(prev, 0, 0, c.width, c.height);
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    setReady(true);
  }, []);

  useEffect(() => {
    if (!open) { setReady(false); return; }
    // Give the Radix dialog a tick to mount so the canvas has non-zero size.
    const r1 = requestAnimationFrame(() => requestAnimationFrame(fit));
    const wrap = wrapperRef.current;
    const ro = wrap && typeof ResizeObserver !== "undefined" ? new ResizeObserver(fit) : null;
    if (ro && wrap) ro.observe(wrap);
    window.addEventListener("resize", fit);
    return () => {
      cancelAnimationFrame(r1);
      if (ro) ro.disconnect();
      window.removeEventListener("resize", fit);
    };
  }, [open, fit]);

  const posOf = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches?.[0] || e.changedTouches?.[0];
    const clientX = t ? t.clientX : e.clientX;
    const clientY = t ? t.clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    if (!ready) return;
    drawing.current = true;
    const pt = posOf(e);
    lastPt.current = pt;
    // Draw a dot so a single tap leaves a mark instead of nothing.
    const ctx = canvasRef.current.getContext("2d");
    ctx.strokeStyle = tool === "eraser" ? "#FFFFFF" : color;
    ctx.fillStyle = tool === "eraser" ? "#FFFFFF" : color;
    const w = tool === "eraser" ? size * 4 : size;
    ctx.lineWidth = w;
    ctx.beginPath();
    ctx.arc(pt.x, pt.y, Math.max(1, w / 2), 0, Math.PI * 2);
    ctx.fill();
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const pt = posOf(e);
    ctx.strokeStyle = tool === "eraser" ? "#FFFFFF" : color;
    ctx.lineWidth = tool === "eraser" ? size * 4 : size;
    ctx.beginPath();
    ctx.moveTo(lastPt.current.x, lastPt.current.y);
    ctx.lineTo(pt.x, pt.y);
    ctx.stroke();
    lastPt.current = pt;
  };

  const end = (e) => {
    if (!drawing.current) return;
    if (e) e.preventDefault?.();
    drawing.current = false;
  };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.restore();
  };

  const save = async () => {
    try {
      setUploading(true);
      const c = canvasRef.current;
      const blob = await new Promise((res) => c.toBlob(res, "image/png", 0.95));
      if (!blob) throw new Error("blob");
      const file = new File([blob], `drawing-${Date.now()}.png`, { type: "image/png" });
      const { path } = await uploadImage(file);
      onSave(path);
      onOpenChange(false);
    } catch (err) {
      toast.error("Could not save drawing");
      console.warn("[drawing] save failed:", err);
    } finally { setUploading(false); }
  };

  const COLORS = ["#1C1917", "#DC2626", "#D97706", "#059669", "#2563EB", "#7C3AED"];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-full p-0 gap-0" data-testid="drawing-pad">
        <DialogHeader className="px-5 pt-5 pb-3 flex-row items-center justify-between space-y-0">
          <DialogTitle className="font-display">Quick drawing</DialogTitle>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-stone-100 dark:hover:bg-stone-800"
            data-testid="draw-close"
            aria-label="Close drawing"
          >
            <X className="h-5 w-5" />
          </button>
        </DialogHeader>

        <div className="px-5">
          <div
            ref={wrapperRef}
            className="w-full h-[55vh] bg-white rounded-xl border border-stone-200 dark:border-stone-700 overflow-hidden"
          >
            <canvas
              ref={canvasRef}
              className="w-full h-full touch-none cursor-crosshair select-none"
              onPointerDown={(e) => { e.currentTarget.setPointerCapture?.(e.pointerId); start(e); }}
              onPointerMove={move}
              onPointerUp={end}
              onPointerLeave={end}
              onPointerCancel={end}
              data-testid="drawing-canvas"
            />
          </div>
        </div>

        <div className="flex items-center flex-wrap gap-2 px-5 py-4 border-t border-border mt-4">
          <button
            onClick={() => setTool("pen")}
            className={`h-9 w-9 inline-flex items-center justify-center rounded-full ${tool === "pen" ? "bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900" : "hover:bg-stone-100 dark:hover:bg-stone-800"}`}
            data-testid="draw-pen"
            aria-label="Pen"
          >
            <Pen className="h-4 w-4" />
          </button>
          <button
            onClick={() => setTool("eraser")}
            className={`h-9 w-9 inline-flex items-center justify-center rounded-full ${tool === "eraser" ? "bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900" : "hover:bg-stone-100 dark:hover:bg-stone-800"}`}
            data-testid="draw-eraser"
            aria-label="Eraser"
          >
            <Eraser className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-1.5 ml-1">
            {COLORS.map((c) => (
              <button
                key={c}
                onClick={() => { setColor(c); setTool("pen"); }}
                aria-label={`Color ${c}`}
                data-testid={`draw-color-${c}`}
                className={`h-6 w-6 rounded-full border-2 ${color === c && tool === "pen" ? "border-stone-900 dark:border-stone-100 scale-110" : "border-stone-300 dark:border-stone-700"}`}
                style={{ background: c }}
              />
            ))}
          </div>
          <input
            type="range"
            min={1}
            max={10}
            value={size}
            onChange={(e) => setSize(Number(e.target.value))}
            className="w-20 ml-2"
            data-testid="draw-size"
          />
          <div className="ml-auto flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={clear} data-testid="draw-clear">
              <Trash2 className="h-4 w-4 mr-1" /> Clear
            </Button>
            <Button onClick={save} disabled={uploading} className="rounded-full" data-testid="draw-save">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              <span className="ml-1">Save</span>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export { fileUrl };
